import { CANONICAL, GIFI } from "../../../../../shared/helpers/constants";

// REV_01, COMP_01 + INT_01..INT_03 — revenue presence, compilation completeness,
// intangible assets and amortization.

export const incomeAndIntangibleCascades = {
	// REV_01 — expenses but no revenue
	"rev01_determine_reason": {
		type: "labelResponse",
		labels: {
			holding_company: "Holding company",
			investment_income: "Investment income only",
			expenses_unmapped: "Expenses unmapped",
			startup: "Startup",
		},
	},
	"rev01_resolve": {
		type: "branch", of: "rev01_determine_reason",
		when: {
			"holding_company": { type: "acknowledge", detail: "Acknowledged — holding company" },
			"investment_income": { type: "acknowledge", detail: "Acknowledged — investment income" },
			"startup": { type: "acknowledge", detail: "Acknowledged — startup" },
			"expenses_unmapped": {
				type: "pickAccount",
				keywords: ["expense", "cost", "rent", "salary", "wages", "operating"],
				target: { canonicalCode: CANONICAL.OPERATING_EXPENSE, canonicalName: "Other expenses" },
				question: "Which expense account?",
				foundNoun: "matching",
				allDetail: "Showing all",
				blockedDetail: "No accounts",
			},
		},
	},

	// COMP_01 — extremely empty compilation
	"comp01_determine_reason": {
		type: "labelResponse",
		labels: {
			dormant: "Dormant entity",
			startup: "Startup",
			holding_company: "Holding company",
			accounts_unmapped: "Accounts unmapped",
		},
	},
	"comp01_resolve": {
		type: "branch", of: "comp01_determine_reason",
		when: {
			"dormant": { type: "acknowledge", detail: "Acknowledged — dormant" },
			"startup": { type: "acknowledge", detail: "Acknowledged — startup" },
			"holding_company": { type: "acknowledge", detail: "Acknowledged — holding company" },
			"accounts_unmapped": {
				type: "pickAccount",
				keywords: ["asset", "liability", "equity", "revenue", "expense"],
				target: { canonicalCode: CANONICAL.CASH, canonicalName: "Other current assets" },
				question: "Which accounts need mapping?",
				foundNoun: "matching",
				allDetail: "Showing all",
				blockedDetail: "No accounts",
			},
		},
	},

	// INT_03 — accumulated amortization overshoot
	"int03_determine_cause": {
		type: "labelResponse",
		labels: {
			writeoff_incomplete: "Write-off incomplete",
			mapping_error: "Mapping error",
			posting_error: "Posting error",
		},
	},
	"int03_resolve": {
		type: "branch", of: "int03_determine_cause",
		when: {
			"writeoff_incomplete": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "int03_show_numbers", amountField: "overshoot", abs: false,
				proposal: {
					debitCanonicalCode: CANONICAL.ACCUMULATED_AMORTIZATION, debitDescription: "Accumulated amortization of intangible assets", debitGifi: GIFI.ACCUMULATED_AMORTIZATION,
					creditCanonicalCode: CANONICAL.LOSS_ON_DISPOSAL, creditDescription: "Realized gains/losses on disposal of assets", creditGifi: GIFI.LOSS_ON_DISPOSAL,
					description: "Correct accumulated amortization overshoot",
				},
			},
			"posting_error": {
				type: "proposeAjeFromPeerStep",
				peerStepId: "int03_show_numbers", amountField: "overshoot", abs: false,
				proposal: {
					debitCanonicalCode: CANONICAL.ACCUMULATED_AMORTIZATION, debitDescription: "Accumulated amortization of intangible assets", debitGifi: GIFI.ACCUMULATED_AMORTIZATION,
					creditCanonicalCode: CANONICAL.AMORTIZATION_EXPENSE, creditDescription: "Amortization of intangible assets (correction)", creditGifi: GIFI.AMORTIZATION_EXPENSE,
					description: "Correct accumulated amortization overshoot",
				},
			},
			"mapping_error": {
				type: "pickAccount",
				keywords: ["software", "patent", "goodwill", "intangible", "amortization"],
				target: { canonicalCode: CANONICAL.INTANGIBLE_SOFTWARE, canonicalName: "Intangible assets" },
				question: "Which account is misclassified?",
				blockedDetail: "No accounts available",
			},
		},
	},

	// INT_02 — amortization expense without intangibles
	"int02_determine_reason": {
		type: "labelResponse",
		labels: {
			intangible_unmapped: "Intangible assets unmapped",
			disposed: "Intangibles disposed",
			misclassified: "Expense should be reclassified",
		},
	},
	"int02_resolve": {
		type: "branch", of: "int02_determine_reason",
		when: {
			"disposed": { type: "acknowledge", detail: "Acknowledged — intangibles disposed" },
			"intangible_unmapped": {
				type: "pickAccount",
				keywords: ["software", "patent", "goodwill", "intangible", "license"],
				target: { canonicalCode: CANONICAL.INTANGIBLE_SOFTWARE, canonicalName: "Intangible assets" },
				question: "Which account is the intangible asset?",
				blockedDetail: "No accounts available",
			},
			"misclassified": {
				type: "pickAccount",
				keywords: ["depreciation", "expense", "operating"],
				target: { canonicalCode: CANONICAL.DEPRECIATION_EXPENSE, canonicalName: "Amortization of tangible assets" },
				question: "Which account should this be reclassified to?",
				blockedDetail: "No accounts available",
			},
		},
	},

	// INT_01 — intangibles without amortization
	"int01_determine_reason": {
		type: "labelResponse",
		labels: {
			indefinite_life: "Indefinite-life (goodwill)",
			fully_amortized: "Fully amortized",
			need_aje: "Amortization needed",
			expense_unmapped: "Expense is unmapped",
		},
	},
	"int01_resolve": {
		type: "branch", of: "int01_determine_reason",
		when: {
			"indefinite_life": { type: "acknowledge", detail: "Acknowledged — indefinite-life, no amortization required" },
			"fully_amortized": { type: "acknowledge", detail: "Acknowledged — fully amortized" },
			"need_aje": {
				type: "amountThenAje",
				amountKey: "int01_resolve_amort_amount",
				promptDetail: "Enter the amortization amount",
				promptQuestion: "Annual amortization expense?",
				inputField: { id: "amort_amount", label: "Amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.AMORTIZATION_EXPENSE, debitDescription: "Amortization of intangible assets", debitGifi: GIFI.AMORTIZATION_EXPENSE,
					creditCanonicalCode: CANONICAL.ACCUMULATED_AMORTIZATION, creditDescription: "Accumulated amortization of intangible assets", creditGifi: GIFI.ACCUMULATED_AMORTIZATION,
					description: "Record amortization of intangible assets",
				},
			},
			"expense_unmapped": {
				type: "pickAccount",
				keywords: ["amortization", "amort", "depreciation", "write-off"],
				target: { canonicalCode: CANONICAL.AMORTIZATION_EXPENSE, canonicalName: "Amortization of intangible assets" },
				question: "Which account should be mapped to Amortization Expense?",
				blockedDetail: "No accounts available",
			},
		},
	},
};
